import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useCallback } from "react";
import { SCheckboxV2 } from "../../SCheckboxV2";
import { useEditorDependencyInjectionContext } from "../providers/EditorDependencyInjectionContext";
import { SOverlaySpinner } from "../../SSpinner";
import { FileUploader } from "../FileUploader";
import { SInputV2 } from "../../SInputV2";
import { SVariablePicker } from "../..";
import { SWStateRecordType } from "@superwall-me/schema";
const VideoPropertyEditor = ({ readOnly, value, onChangeValue, onClickValue })=>{
    const { useVariablePickerItems, useUploadFile, requiresLiquid, getRequiredPaths, useProcessVideo } = useEditorDependencyInjectionContext();
    const items = useVariablePickerItems();
    const [hasFocus, setHasFocus] = useState(false);
    const inputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState();
    const { mutateAsync: upload, isError, error, reset: resetUploadFn } = useUploadFile();
    const { processVideo } = useProcessVideo();
    const [isLoading, setIsLoading] = useState(false);
    const defaultPlaceholder = "e.g. http://example.com/video.mp4";
    const placeholder = defaultPlaceholder;
    const setVideoProps = useCallback((newProps)=>{
        let defaultProps = {
            type: "property-video",
            src: "",
            autoplay: false,
            loop: false,
            muted: false,
            controls: false
        };
        const updatedValue = {
            ...defaultProps,
            ...value,
            ...newProps
        };
        if (requiresLiquid(updatedValue.src)) {
            const requiredRecords = getRequiredPaths(updatedValue.src).map((x)=>SWStateRecordType.createId(x));
            onChangeValue?.({
                ...updatedValue,
                thumbnail: updatedValue.thumbnail,
                sources: [],
                rendering: {
                    type: "liquid",
                    requiredStateIds: requiredRecords
                }
            });
            return;
        }
        onChangeValue?.({
            ...updatedValue,
            thumbnail: updatedValue.thumbnail,
            sources: [],
            rendering: {
                type: "literal"
            }
        });
    }, [
        onChangeValue,
        value,
        requiresLiquid,
        getRequiredPaths
    ]);
    const uploadFile = async (file)=>{
        setIsLoading(true);
        setSelectedFile(file);
        console.log("uploading file", file);
        try {
            const processedVideo = await processVideo(file);
            console.log("Video Compression Rate", processedVideo.video.size / file.size);
            const [videoResp, thumbnailResp, baseResponse] = await Promise.all([
                upload({
                    file: processedVideo.video
                }),
                upload({
                    file: processedVideo.thumbnail
                }),
                upload({
                    file
                })
            ]);
            const { url: videoUrl } = videoResp.result;
            const { url: thumbnailUrl } = thumbnailResp.result;
            setVideoProps({
                src: baseResponse.result.url,
                thumbnail: thumbnailUrl,
                sources: [
                    {
                        src: videoUrl,
                        type: processedVideo.video.type
                    }
                ]
            });
        } catch (e) {
            console.log("error loading video", e);
            try {
                // Fallback to uploading the file
                const resp = await upload({
                    file
                });
                const { url } = resp.result;
                setVideoProps({
                    src: url
                });
            } catch (e) {
                console.log("error loading video", e);
            }
        } finally{
            setIsLoading(false);
        }
    };
    const onHandleUpload = async (file)=>{
        setSelectedFile(file);
        await uploadFile(file);
    };
    const onRetryUpload = async (e)=>{
        e.preventDefault();
        await uploadFile(selectedFile);
    };
    const onInputChange = (e)=>{
        e.preventDefault();
        setVideoProps({
            src: e.target.value
        });
        if (isError) resetUploadFn();
    };
    return /*#__PURE__*/ _jsx("div", {
        onClick: onClickValue,
        children: /*#__PURE__*/ _jsxs("div", {
            className: readOnly ? "pointer-events-none" : "",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-[1fr,auto] items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SInputV2, {
                            value: value?.src || "",
                            onChange: onInputChange,
                            placeholder: placeholder,
                            ref: inputRef,
                            onFocus: ()=>setHasFocus(true),
                            onBlur: ()=>setTimeout(()=>{
                                    setHasFocus(false);
                                }, 1000)
                        }),
                        /*#__PURE__*/ _jsx(FileUploader, {
                            ariaLabel: "Video Upload",
                            handleFile: onHandleUpload,
                            accept: "video/*",
                            hasError: !!error,
                            isSuiButton: true,
                            suiButtonProps: {
                                variant: "primary",
                                className: "h-[34px] !rounded-md "
                            },
                            isLoading: isLoading,
                            handleRetry: onRetryUpload
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-xs opacity-50 pt-1",
                    children: "Upload might take a while. Please keep this tab open."
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-3 pt-3 pb-2 gap-3 gap-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SCheckboxV2, {
                            value: value?.autoplay,
                            onChange: (val)=>{
                                setVideoProps({
                                    autoplay: val,
                                    muted: val ? true : value?.muted || false,
                                    controls: !val ? true : value?.controls || false
                                });
                            },
                            children: "Autoplay"
                        }),
                        /*#__PURE__*/ _jsx(SCheckboxV2, {
                            value: value?.loop,
                            onChange: (loop)=>{
                                setVideoProps({
                                    loop
                                });
                            },
                            children: "Loop"
                        }),
                        /*#__PURE__*/ _jsx(SVariablePicker, {
                            items: items,
                            asButton: true,
                            enableHotkey: hasFocus,
                            didSelect: (item)=>{
                                let text = value?.src || "";
                                if (inputRef.current) {
                                    const { selectionStart, selectionEnd } = inputRef.current;
                                    const beforeText = text.substring(0, selectionStart || 0).trimEnd();
                                    const textToInsert = item.liquid?.trim() || "";
                                    const rhs = text.substring(selectionEnd || 0, text.length).trimStart();
                                    const lhs = `${beforeText}${textToInsert}`;
                                    const newPosition = lhs.length;
                                    text = `${lhs}${rhs}`;
                                    setVideoProps({
                                        src: text
                                    });
                                    setTimeout(()=>{
                                        inputRef.current?.focus();
                                        if (inputRef.current) {
                                            inputRef.current.selectionStart = newPosition;
                                            inputRef.current.selectionEnd = newPosition;
                                        }
                                    }, 100);
                                } else {
                                    setVideoProps({
                                        src: `${value?.src || ""}${item.liquid?.trim()}`
                                    });
                                }
                            },
                            didClose: ()=>{}
                        }),
                        /*#__PURE__*/ _jsx(SCheckboxV2, {
                            value: value?.muted,
                            disabled: value?.autoplay,
                            onChange: (muted)=>{
                                setVideoProps({
                                    muted
                                });
                            },
                            children: "Muted"
                        }),
                        /*#__PURE__*/ _jsx(SCheckboxV2, {
                            value: value?.controls,
                            disabled: !value?.autoplay,
                            onChange: (controls)=>{
                                setVideoProps({
                                    controls
                                });
                            },
                            children: "Controls"
                        })
                    ]
                }),
                isError && /*#__PURE__*/ _jsx("div", {
                    children: error?.message
                }),
                value?.src && !value?.src.startsWith("https://user-content.superwalleditor.com/") && /*#__PURE__*/ _jsx("p", {
                    className: "text-sm opacity-50",
                    children: "External video URLs may impact performance and incur CDN costs. For best results, upload videos directly to Superwall."
                }),
                isLoading && /*#__PURE__*/ _jsx(SOverlaySpinner, {})
            ]
        })
    });
};
export default VideoPropertyEditor;
